import { Route, Routes, useLocation } from "react-router-dom";
import LegacySettingsRedirect from "../components/LegacySettingsRedirect";
import AppLayout from "../components/AppLayout";
// import DashboardNavbar from "../components/DashboardNavbar";
// import Footer from "../components/Footer";
import Dashboard from "./Dashboard";
import Activity from "./Activity";
import CreditLimitInfo from "./CreditLimitInfo";
import ClaimCreditLimit from "./ClaimCreditLimit";
import LoanPreApproval from "./LoanPreApproval";
import PayBills from "./PayBills";
import Pin from "./Pin";
import Settings from "./Settings";
import TripBookingWebView from "../components/TripBookingWebView";
import Aviation from "./Aviation";
import FlightBookingChoice from "./aviation/FlightBookingChoice";
import Fund from "./Fund";
import Payments from "./Payments";
import withFullOnboarding from "../middleware/withFullOnboarding";
import Send from "./Send";
import Cards from "./Cards";
import ComingSoon from "./ComingSoon";
import Marketplace from "./Marketplace";
import Merchants from "./marketplace/Merchants";
import Cart from "../components/Cart";
import { AnimatePresence } from "framer-motion";
import { useEffect } from "react";
import Services from "./Services";
import VerifyBVN from "../components/VerifyBVN";
import VerifyNIN from "../components/VerifyNIN";
import VerifyBank from "../components/VerifyBank";
import UploadProfilePictureModal from "../components/UploadProfilePictureModal";
import TestLiveness from "./TestLiveness";
import Support from "./Support";
import SalaryAdvance from "./SalaryAdvance";
import Referrals from "./Referrals";
import CheckoutResumeBanner from "../components/CheckoutResumeBanner";

interface IHomeProps {
  openMarketplace?: boolean;
}

const Home = ({ openMarketplace = true }: IHomeProps) => {
	const location = useLocation();
	const state = location.state as { background?: Location; name?: string };

	useEffect(() => {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: "smooth",
		});
	}, [location]);

	// Hide the Tawk.to live-chat widget across the authenticated dashboard; it
	// should only appear on the marketing/landing pages. Restore it on unmount
	// (i.e. when the user navigates away from the dashboard).
	useEffect(() => {
		const w = window as unknown as {
			Tawk_API?: {
				hideWidget?: () => void;
				showWidget?: () => void;
				onLoad?: () => void;
			};
		};

		const hide = () => {
			try {
				w.Tawk_API?.hideWidget?.();
			} catch {
				/* widget not loaded yet */
			}
		};

		if (w.Tawk_API?.hideWidget) {
			hide();
		} else {
			// Tawk hasn't finished loading — hook onLoad, preserving any existing handler.
			w.Tawk_API = w.Tawk_API || {};
			const prevOnLoad = w.Tawk_API.onLoad;
			w.Tawk_API.onLoad = () => {
				if (typeof prevOnLoad === "function") prevOnLoad();
				hide();
			};
		}

		return () => {
			try {
				w.Tawk_API?.showWidget?.();
			} catch {
				/* ignore */
			}
		};
	}, []);

	return (
		<AppLayout>
			<CheckoutResumeBanner />
			<Routes location={state?.background || location}>
				<Route path="home" element={<Dashboard />} />
				<Route path="/" element={<Dashboard />} />
				<Route path="send/*" element={<Send />} />
				<Route path="fund/*" element={<Fund />} />
				{/* Chooser shown before the aviation flow; more specific than aviation/* so it wins. Sits outside Aviation's card gate so the Travels option stays reachable. */}
				<Route
					path="aviation/start"
					element={<FlightBookingChoice />}
				/>
				<Route path="aviation/*" element={<Aviation />} />
				<Route path="services/*" element={<Services />} />

				{openMarketplace ? (
					<>
						<Route
							path="marketplace/merchants"
							element={<Merchants />}
						/>
						<Route path="marketplace/*" element={<Marketplace />} />
					</>
				) : (
					<Route path="marketplace/*" element={<ComingSoon />} />
				)}

				<Route path="activity" element={<Activity />} />
				<Route path="credit-limit-info" element={<CreditLimitInfo />} />
				<Route path="credit-limit/claim" element={<ClaimCreditLimit />} />
				<Route path="loans/request" element={<LoanPreApproval />} />
				<Route path="cards" element={<Cards />} />
				<Route path="card/*" element={<Cards />} />
				<Route path="wallet" element={<Cards />} />
				<Route path="paybills/*" element={<PayBills />} />
				<Route path="pin/*" element={<Pin />} />
				<Route path="settings/*" element={<LegacySettingsRedirect />} />
				<Route path="home/settings/*" element={<Settings />} />
				<Route path="payments/*" element={<Payments />} />
				<Route path="support" element={<Support />} />
				<Route path="support/:id" element={<Support />} />
				<Route path="home/support" element={<Support />} />
				<Route path="home/support/:id" element={<Support />} />
				<Route path="salary-advance/*" element={<SalaryAdvance />} />
				<Route path="referrals" element={<Referrals />} />
				<Route path="trip-booking/" element={<TripBookingWebView />} />
				<Route path="/cart" element={<Cart />} />
				<Route path="/verify-bvn" element={<VerifyBVN />} />
				<Route path="/verify-bank" element={<VerifyBank />} />
				<Route path="/liveness-test-debug" element={<TestLiveness />} />
			</Routes>
			<AnimatePresence>
				{state?.background && state.name === "cart" && (
					<Cart key="cart-modal" />
				)}
				{state?.background && state.name === "verify-bvn" && (
					<VerifyBVN key="verify-bvn-modal" />
				)}
				{state?.background && state.name === "verify-nin" && (
					<VerifyNIN key="verify-nin-modal" />
				)}
				{state?.background && state.name === "verify-bank" && (
					<VerifyBank key="verify-bank-modal" />
				)}
				{state?.background &&
					state.name === "upload-profile-picture" && (
						<UploadProfilePictureModal key="upload-profile-picture-modal" />
					)}
			</AnimatePresence>
		</AppLayout>
	);
};

export default withFullOnboarding()(Home);
